// /series/:seriesid — show page: header + episode list. Loader-only ⇒ pure SSG (see series.tsx).
// Node-prerender-safe: fetch + MUI + local helpers only.
import { Link as RouterLink } from 'react-router';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Mui from '../../src/ui/Setup/Mui';
import Markdown from './series-markdown';
import SubscribePanel from './series-subscribe';
import { fetchShow, formatDuration, formatUpdated, episodeTitle, type ShowDetail } from './series-data';
import { metaDescription, breadcrumbLdJson, buildPodcastSeriesLd, seriesUrl } from './series-ld';
import SeriesNotFound, { NOT_FOUND_META } from './series-notfound';
import type { Route } from './+types/series.$seriesid';

export async function loader({ params }: Route.LoaderArgs): Promise<{ show: ShowDetail | null }> {
  // A missing slug already returns null without throwing, so reaching this catch means the artifact fetch itself broke — at prerender that bakes a "not found" page for a show that exists. Log; do NOT throw (that fails the whole build).
  try { return { show: await fetchShow(params.seriesid) }; }
  catch (e) { console.warn('[series] show fetch failed:', params.seriesid, e); return { show: null }; }
}

// Only `pages`+`featured` shows are prerendered (directory.ts `buildListedShows`), so the great
// majority of migrated shows — `feed` tier, RSS-only — have no static page. CloudFront serves
// the SPA fallback for those, the router then re-runs this route's loader client-side, and the
// server-loader `.data` fetch comes back as that fallback's HTML: RR throws "Unable to decode
// turbo-stream response" and the visitor gets a raw error screen. Catching it into the same
// `show:null` landing is what makes /podcaster/:slug's slug-preserving 301 safe for every show.
// NOT `hydrate: true` — prerendered pages keep their baked loaderData, so SSG/SEO is untouched.
// Deliberately SILENT, unlike the server loader's `console.warn` above: that decode error is the EXPECTED outcome for the feed-tier majority, so warning here would fire on the common path and drown the CDN blip it was meant to surface. A blip and an absent show land the visitor in the same honest place either way.
export async function clientLoader({ serverLoader }: Route.ClientLoaderArgs) {
  try { return await serverLoader(); }
  catch { return { show: null }; }
}

export const meta: Route.MetaFunction = ({ loaderData }) => {
  const show = loaderData?.show;
  if (!show) return NOT_FOUND_META;
  const title = `${show.title} — Gnothi Series`;
  const description = metaDescription(show.description);
  const url = seriesUrl(show.slug);
  const tags = [
    { title }, { name: 'description', content: description },
    { property: 'og:title', content: title }, { property: 'og:description', content: description },
    { property: 'og:image', content: show.cover_url },
    { property: 'og:url', content: url }, { tagName: 'link', rel: 'canonical', href: url },
  ];
  // The noindex gate IS the visibility mechanism (D3): a non-`featured` show gets a real page,
  // just absent from search + no structured data. Blocklist-shaped on purpose — an unknown or
  // stale-vocabulary tier falls to noindex, the conservative side.
  if (show.privacy !== 'featured') return [...tags, { name: 'robots', content: 'noindex,follow' }];
  return [
    ...tags,
    { 'script:ld+json': buildPodcastSeriesLd(show) },
    { 'script:ld+json': breadcrumbLdJson([{ name: show.title, path: `/series/${show.slug}` }]) },
  ];
};

export default function SeriesShow({ loaderData }: Route.ComponentProps) {
  const { show } = loaderData;

  if (!show) return <SeriesNotFound />;

  const updated = formatUpdated(show.updated_at);
  const stats = [
    `${show.episodes.length} episode${show.episodes.length === 1 ? '' : 's'}`,
    updated && `updated ${updated}`,
  ].filter(Boolean).join(' · ');

  return (
    <Mui>
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { sm: 'center' } }}>
            <Box component="img" src={show.cover_url} alt={show.image_alt || show.title}
              sx={{ width: 120, height: 120, borderRadius: 2, objectFit: 'cover', bgcolor: 'action.hover' }} />
            <Stack spacing={0.5}>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>{show.title}</Typography>
              {show.owner_name && <Typography color="text.secondary">by {show.owner_name}</Typography>}
              <Typography variant="caption" color="text.secondary">{stats}</Typography>
              {show.category && <Chip size="small" label={show.category} sx={{ alignSelf: 'flex-start' }} />}
            </Stack>
          </Stack>
          {/* AI-generated, so it carries markdown too (A9) — same untrusted renderer, same one-h1 gate as the episode page. */}
          {show.description && <Box color="text.secondary"><Markdown>{show.description}</Markdown></Box>}
          <SubscribePanel feedUrl={show.feed_url} links={show.subscribe_links} />
          <EpisodeList show={show} />
        </Stack>
      </Container>
    </Mui>
  );
}

function EpisodeList({ show }: { show: ShowDetail }) {
  if (!show.episodes.length) {
    return <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No episodes yet.</Typography>;
  }
  return (
    <Stack spacing={1.5}>
      {show.episodes.map((e) => {
        const duration = formatDuration(e.final_duration_seconds);
        return (
          <Card key={e.id} variant="outlined">
            <CardActionArea component={RouterLink} to={`/series/${show.slug}/${e.id}`} sx={{ p: 2 }}>
              <Typography sx={{ fontWeight: 600 }}>{episodeTitle(e)}</Typography>
              {e.teaser && <Typography variant="body2" color="text.secondary">{e.teaser}</Typography>}
              {duration && <Typography variant="caption" color="text.secondary">{duration}</Typography>}
            </CardActionArea>
          </Card>
        );
      })}
    </Stack>
  );
}
